import pino from 'pino';

// Speichere die originalen console-Methoden bevor wir sie überschreiben
const originalConsole = {
  debug: window.console.debug.bind(window.console),
  log: window.console.log.bind(window.console),
  info: window.console.info.bind(window.console),
  warn: window.console.warn.bind(window.console),
  error: window.console.error.bind(window.console)
};

// Erkennung des Produktionsmodus - mehrere Methoden verwenden
const isProduction = 
  process.env.NODE_ENV === 'production' || 
  window.location.hostname !== 'localhost';

// Zentrale Logger‑Instanz
const logger = pino({
  level: isProduction ? 'error' : 'debug', // In Entwicklung debug, in Production nur Fehler
  browser: { 
    asObject: true,
    // In Produktion keine Konsolenausgabe
    transmit: {
      level: 'error',
      send: (level, logEvent) => {
        // In Produktion nur tatsächlich loggen, wenn es wichtige Fehler sind
        if (isProduction && level !== 'error') {
          return;
        }
        
        // Filtere bekannte harmlose Errors aus
        const msg = logEvent.messages[0];
        if (isProduction && typeof msg === 'string') {
          // Firebase Auth/Firestore connection errors sind oft harmlos
          if (msg.includes('ERR_SOCKET_NOT_CONNECTED') || 
              msg.includes('securetoken.googleapis.com') ||
              msg.includes('identitytoolkit.googleapis.com') ||
              msg.includes('firestore.googleapis.com') ||
              msg.includes('Bad Request') ||
              msg.includes('webchannel_blob') ||
              msg.includes('gsessionid')) {
            return; // Nicht loggen
          }
        }
        
        // Standard Pino Konsolen-Ausgabe über die originale Konsole
        // um Rekursionen zu vermeiden
        const args = logEvent.messages.length > 1 ? logEvent.messages.slice(1) : [];
        // Type-Safe console Zugriff
        const consoleMethod = level === 'fatal' ? 'error' : level;
        if (consoleMethod in originalConsole) {
          (originalConsole as any)[consoleMethod](msg, ...args);
        }
      }
    }
  },
  redact: [
    'bankDetails.*',
    'iban',
    'bic',
    'buyerName',
    'pdfPath',
    'globalId',
    'globalIdScheme',
  ],
  // Erlaube multiple Parameter für Logger-Methoden
  formatters: {
    log(object: any) {
      return object;
    }
  }
});

// In Produktions‑Builds sämtliche console.*‑Aufrufe (außer warn/error) de‑nopen
if (isProduction) {
  const noop = () => undefined;
  console.debug = noop;
  console.log = noop;
  console.info = noop;
  // Kritische Fehler trotzdem an den Logger senden, aber sicher über die originale Konsole
  console.warn = (...args: unknown[]) => {
    logger.warn(args);
    // Keine Rekursion mehr möglich, da wir die ursprüngliche Methode verwenden
    // originalConsole.warn(...args);
  };
  console.error = (...args: unknown[]) => {
    logger.error(args);
    // Keine Rekursion mehr möglich, da wir die ursprüngliche Methode verwenden
    // originalConsole.error(...args);
  };
} else {
  // In Development: leite console.* an den Logger weiter, aber sicher
  console.debug = (...args: unknown[]) => {
    logger.debug(args);
    // Keine Rekursion mehr möglich, da wir die ursprüngliche Methode verwenden
    // originalConsole.debug(...args);
  };
  console.log = (...args: unknown[]) => {
    logger.debug(args);
    // Keine Rekursion mehr möglich, da wir die ursprüngliche Methode verwenden
    // originalConsole.log(...args);
  };
  console.info = (...args: unknown[]) => {
    logger.info(args);
    // Keine Rekursion mehr möglich, da wir die ursprüngliche Methode verwenden
    // originalConsole.info(...args);
  };
  console.warn = (...args: unknown[]) => {
    logger.warn(args);
    // Keine Rekursion mehr möglich, da wir die ursprüngliche Methode verwenden
    // originalConsole.warn(...args);
  };
  console.error = (...args: unknown[]) => {
    logger.error(args);
    // Keine Rekursion mehr möglich, da wir die ursprüngliche Methode verwenden
    // originalConsole.error(...args);
  };
}

// Exportiere das originalConsole-Objekt, aber filtere es in der Produktion
const filteredNativeConsole = {
  debug: isProduction ? () => {} : originalConsole.debug,
  log: isProduction ? () => {} : originalConsole.log,
  info: isProduction ? () => {} : originalConsole.info,
  warn: originalConsole.warn, // Warnungen immer anzeigen
  error: originalConsole.error, // Fehler immer anzeigen
};

export { filteredNativeConsole as nativeConsole };

// Erstelle einen Logger-Wrapper mit flexibler Signatur
interface EnhancedLogger {
  isProduction: boolean;
  debug(message: string | any, ...args: any[]): void;
  debug(obj: any, message?: string): void;
  info(message: string | any, ...args: any[]): void;
  info(obj: any, message?: string): void;
  warn(message: string | any, ...args: any[]): void;
  warn(obj: any, message?: string): void;
  error(message: string | any | Error | unknown, ...args: any[]): void;
  error(obj: any, message?: string): void;
  fatal(message: string | any, ...args: any[]): void;
  fatal(obj: any, message?: string): void;
}

// Wrapper-Funktionen die sowohl Strings als auch Objekte handhaben können
const loggerWrapper = {
  isProduction,
  debug: (messageOrObj: any, objOrMessage?: any) => {
    if (typeof messageOrObj === 'string') {
      // Erster Parameter ist String = Nachricht
      if (objOrMessage !== undefined) {
        // Zweiter Parameter ist Objekt/Context
        logger.debug(typeof objOrMessage === 'object' ? objOrMessage : { context: objOrMessage }, messageOrObj);
      } else {
        // Nur String-Nachricht
        logger.debug(messageOrObj);
      }
    } else {
      // Erster Parameter ist Objekt
      logger.debug(messageOrObj, objOrMessage || 'Debug message');
    }
  },
  info: (messageOrObj: any, objOrMessage?: any) => {
    if (typeof messageOrObj === 'string') {
      // Erster Parameter ist String = Nachricht
      if (objOrMessage !== undefined) {
        // Zweiter Parameter ist Objekt/Context
        logger.info(typeof objOrMessage === 'object' ? objOrMessage : { context: objOrMessage }, messageOrObj);
      } else {
        // Nur String-Nachricht
        logger.info(messageOrObj);
      }
    } else {
      // Erster Parameter ist Objekt
      logger.info(messageOrObj, objOrMessage || 'Info message');
    }
  },
  warn: (messageOrObj: any, objOrMessage?: any) => {
    if (typeof messageOrObj === 'string') {
      // Erster Parameter ist String = Nachricht
      if (objOrMessage !== undefined) {
        // Zweiter Parameter ist Objekt/Context
        logger.warn(typeof objOrMessage === 'object' ? objOrMessage : { context: objOrMessage }, messageOrObj);
      } else {
        // Nur String-Nachricht
        logger.warn(messageOrObj);
      }
    } else {
      // Erster Parameter ist Objekt
      logger.warn(messageOrObj, objOrMessage || 'Warning message');
    }
  },
  error: (messageOrObj: any, objOrMessage?: any) => {
    if (typeof messageOrObj === 'string') {
      // Erster Parameter ist String = Nachricht
      if (objOrMessage !== undefined) {
        // Zweiter Parameter ist Error/Objekt/Context
        if (objOrMessage instanceof Error) {
          logger.error({ err: objOrMessage }, messageOrObj);
        } else if (typeof objOrMessage === 'object') {
          logger.error(objOrMessage, messageOrObj);
        } else {
          logger.error({ context: objOrMessage }, messageOrObj);
        }
      } else {
        // Nur String-Nachricht
        logger.error(messageOrObj);
      }
    } else {
      // Erster Parameter ist Error/Objekt
      if (messageOrObj instanceof Error) {
        logger.error({ err: messageOrObj }, objOrMessage || messageOrObj.message);
      } else {
        logger.error(messageOrObj, objOrMessage || 'Error occurred');
      }
    }
  },
  fatal: (messageOrObj: any, objOrMessage?: any) => {
    if (typeof messageOrObj === 'string') {
      // Erster Parameter ist String = Nachricht
      if (objOrMessage !== undefined) {
        // Zweiter Parameter ist Error/Objekt/Context
        if (objOrMessage instanceof Error) {
          logger.fatal({ err: objOrMessage }, messageOrObj);
        } else if (typeof objOrMessage === 'object') {
          logger.fatal(objOrMessage, messageOrObj);
        } else {
          logger.fatal({ context: objOrMessage }, messageOrObj);
        }
      } else {
        // Nur String-Nachricht
        logger.fatal(messageOrObj);
      }
    } else {
      // Erster Parameter ist Error/Objekt
      if (messageOrObj instanceof Error) {
        logger.fatal({ err: messageOrObj }, objOrMessage || messageOrObj.message);
      } else {
        logger.fatal(messageOrObj, objOrMessage || 'Fatal error occurred');
      }
    }
  }
} as const;

// Stelle sicher, dass Tree‑Shaking nicht den Import entfernt
export default loggerWrapper; 